/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {
  ChangeEvent,
  KeyboardEvent,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import debounce from 'lodash.debounce';

export interface SearchOverlayInputState {
  handleClose: () => void;
  handleInputChange: (event: ChangeEvent<HTMLInputElement>) => void;
  handleSearchKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
  inputRef: RefObject<HTMLInputElement>;
  searchValue: string;
}

/**
 * Tracks search overlay input value, autofocus, and debounced changes.
 *
 * ```typescript
 * import useSearchOverlayInput from '@ion/dls/src/components/search/useSearchOverlayInput';
 *
 * ...
 *
 * const {inputRef, searchValue, handleInputChange} = useSearchOverlayInput({
 *   open,
 *   onChange,
 *   onClose
 * });
 * ```
 *
 * @param params Defines the open state and search callbacks.
 * @returns Input state and handlers for the overlay search box.
 */
export const useSearchOverlayInput = ({
  onChange,
  onClose,
  open,
  minSearchLength = 0
}: {
  onChange: (value: string) => void;
  onClose: () => void;
  open: boolean;
  // the shortest term that is searched - a shorter one (but not an empty
  // one, which clears the search) is never sent ...
  minSearchLength?: number;
}): SearchOverlayInputState => {
  const inputRef                = useRef<HTMLInputElement>(null);
  const [searchValue, setValue] = useState('');

  const debouncedChange = useMemo(() => debounce((value: string) => {
    onChange(value);
  }, 300), [onChange]);

  useEffect(() => {
    if (open) {
      setValue('');
      window.requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
    }
  }, [open]);

  useEffect(() => () => {
    debouncedChange.cancel();
  }, [debouncedChange]);

  const handleClose = useCallback(() => {
    debouncedChange.cancel();
    setValue('');
    onClose();
  }, [debouncedChange, onClose]);

  const handleInputChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const value      = event.target.value;
    const termLength = value.trim().length;
    setValue(value);
    if (!termLength || termLength >= minSearchLength) {
      debouncedChange(value);
    } else {
      debouncedChange.cancel();
    }
  }, [debouncedChange, minSearchLength]);

  const handleSearchKeyDown = useCallback((event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      handleClose();
    }
  }, [handleClose]);

  return {
    handleClose,
    handleInputChange,
    handleSearchKeyDown,
    inputRef,
    searchValue
  };
};

export default useSearchOverlayInput;
